import type { Dispatch, SetStateAction } from 'react';
import { useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import styled, { css } from 'styled-components';
import { StoreItem } from '@storefront/lib-global/globalComponents/storeItems/StoreItem';
import { colorizeFeatureTag } from '@storefront/lib-global/globalComponents/storeItems/utils';
import { useStar2 } from '@storefront/lib-global/hooks/useStar2';
import { TypographyStyles, colours, focusOutline, media, spacing } from '@storefront/lib-global/stylings';
import type { AccordionProps, CommonStoreItemProps } from '@storefront/lib-global/types';
import { getStoreLocatorLink } from '@storefront/lib-global/utils/storeLocator';

interface StoreLocatorItemProps extends CommonStoreItemProps, AccordionProps {
  isMultipleTags: boolean;
  setZoomedStore: Dispatch<SetStateAction<string>>;
}

const S = {
  TypeTag: styled.span<{ $bgColor?: string; $color?: string }>`
    ${TypographyStyles.Label.Large.Medium}
    background-color: ${({ $bgColor }) => $bgColor};
    color: ${({ $color }) => $color || colours.WHITE};
    padding: ${spacing.XXXS};
    text-transform: uppercase;
    white-space: nowrap;
    @media ${media.lessThan('sm')} {
      ${TypographyStyles.Label.Small.Medium}
    }
  `,
  DetailsLink: styled.a<{ $isStar2Locale?: boolean }>`
    ${TypographyStyles.Button.MontserratSemiBold}
    align-self: flex-end;
    background-color: ${colours.BLACK};
    border: 1px solid ${colours.BLACK};
    color: ${colours.WHITE};
    cursor: pointer;
    padding: ${spacing.XXS} ${spacing.S};
    text-decoration: none;
    white-space: nowrap;

    &:hover {
      background-color: ${colours.WHITE};
      color: ${colours.BLACK};

      & svg path {
        fill: ${colours.BLACK};
      }
    }
    &:focus {
      /* Empty focus state to satisfy linter */
    }
    &:focus-visible {
      ${focusOutline}
    }

    @media ${media.lessThan('sm')} {
      text-align: center;
      width: 100%;
    }

    @media ${media.between('lg', 'xl')} {
      ${({ $isStar2Locale }) =>
        $isStar2Locale &&
        css`
          text-align: center;
          width: 100%;
        `}
    }
  `,
  StoreListItem: styled.li`
    list-style: none;
  `,
};

export function StoreLocatorItem(props: StoreLocatorItemProps) {
  const {
    features,
    isScheduleGrouped,
    isActiveStore,
    setActiveStoreId,
    id,
    distance,
    isMultipleTags,
    setZoomedStore,
    displayName,
    address,
  } = props;
  const { t } = useTranslation('lib-global-common');
  const { locale } = useRouter();
  const { isStar2 } = useStar2();

  const storeTags = useMemo(
    () =>
      features
        ?.filter((item) => item.isActive)
        .map(({ name }) => {
          const { bgColor, fontColor } = colorizeFeatureTag(name);
          return (
            <S.TypeTag key={name} $bgColor={bgColor} $color={fontColor}>
              {t(`store.feature.${name}`)}
            </S.TypeTag>
          );
        }),
    [features, t],
  );

  return (
    <S.StoreListItem>
      <StoreItem
        {...props}
        isScheduleGrouped={isScheduleGrouped}
        isActiveStore={isActiveStore}
        setActiveStoreId={setActiveStoreId}
        setZoomedStore={setZoomedStore}
        id={id}
        isColumnView={isMultipleTags}
        distance={distance}
        storeTags={storeTags?.slice(0, 10)}
        actionBtn={
          <Link href={getStoreLocatorLink(address, id, true, locale)} passHref>
            <S.DetailsLink
              tabIndex={0}
              onClick={(e) => e.stopPropagation()}
              aria-label={`${t('see.store.details')}, ${displayName}`}
              data-testid="storeDetailsLink"
              $isStar2Locale={!!isStar2}
            >
              {t('see.store.details')}
            </S.DetailsLink>
          </Link>
        }
      />
    </S.StoreListItem>
  );
}
